import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatItem } from '../core/types';
import type { FeedEngine, ScrollState, ViewportInsets } from '../engine';

const SCROLLBAR_FADE_DELAY_MS = 2_000;

/** Registers and renders the custom engine scrollbar for fine-pointer devices. */
export function FeedScrollbar({
  engine,
  scrollerId,
  scrollerRef,
  scrollState,
  viewportInsets,
}: {
  readonly engine: FeedEngine<ChatItem>;
  readonly scrollerId: string;
  readonly scrollerRef: React.RefObject<HTMLDivElement | null>;
  readonly scrollState: ScrollState;
  readonly viewportInsets: ViewportInsets;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerInsideRef = useRef(false);
  const focusedRef = useRef(false);
  const draggingRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const isFinePointer = useFinePointer();
  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current === null) return;
    clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = null;
  }, []);
  const scheduleFade = useCallback(() => {
    clearFadeTimer();
    if (pointerInsideRef.current || focusedRef.current || draggingRef.current) return;

    fadeTimerRef.current = setTimeout(() => {
      fadeTimerRef.current = null;
      if (pointerInsideRef.current || focusedRef.current || draggingRef.current) return;
      setIsVisible(false);
    }, SCROLLBAR_FADE_DELAY_MS);
  }, [clearFadeTimer]);
  const revealScrollbar = useCallback(() => {
    clearFadeTimer();
    if (!isScrollable(scrollerRef.current)) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
    scheduleFade();
  }, [clearFadeTimer, scheduleFade, scrollerRef]);
  const holdScrollbar = useCallback(() => {
    clearFadeTimer();
    setIsVisible(isScrollable(scrollerRef.current));
  }, [clearFadeTimer, scrollerRef]);
  const handleTrackPointerEnter = useCallback(() => {
    pointerInsideRef.current = true;
    holdScrollbar();
  }, [holdScrollbar]);
  const handleTrackPointerLeave = useCallback(() => {
    pointerInsideRef.current = false;
    scheduleFade();
  }, [scheduleFade]);
  const handleTrackPointerDown = useCallback(() => {
    revealScrollbar();
  }, [revealScrollbar]);
  const handleThumbFocus = useCallback(() => {
    focusedRef.current = true;
    holdScrollbar();
  }, [holdScrollbar]);
  const handleThumbBlur = useCallback(() => {
    focusedRef.current = false;
    scheduleFade();
  }, [scheduleFade]);
  const registerTrack = useCallback(
    (el: HTMLDivElement | null) => {
      trackRef.current = el;
      engine.registerScrollbarElements(trackRef.current, thumbRef.current);
    },
    [engine],
  );
  const registerThumb = useCallback(
    (el: HTMLDivElement | null) => {
      thumbRef.current = el;
      engine.registerScrollbarElements(trackRef.current, thumbRef.current);
    },
    [engine],
  );
  const synthesizeKeyboardDrag = useCallback(
    (scrollDeltaPx: number) => {
      const scroller = scrollerRef.current;
      const track = trackRef.current;
      const thumb = thumbRef.current;
      if (!scroller || !track || !thumb) return;

      const scrollableH = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      const trackRect = track.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const trackTravelH = Math.max(0, trackRect.height - thumbRect.height);
      if (scrollableH <= 0 || trackTravelH <= 0) return;

      const pointerClientY = thumbRect.top + thumbRect.height / 2;
      const pointerDeltaY = (scrollDeltaPx * trackTravelH) / scrollableH;
      engine.beginThumbDrag(pointerClientY);
      engine.updateThumbDrag(pointerClientY + pointerDeltaY);
      engine.endThumbDrag();
    },
    [engine, scrollerRef],
  );
  const handleThumbPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      draggingRef.current = true;
      holdScrollbar();
      engine.beginThumbDrag(event.clientY);
    },
    [engine, holdScrollbar],
  );
  const handleThumbPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
      holdScrollbar();
      engine.updateThumbDrag(event.clientY);
    },
    [engine, holdScrollbar],
  );
  const handleThumbPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      engine.endThumbDrag();
      draggingRef.current = false;
      scheduleFade();
    },
    [engine, scheduleFade],
  );
  const handleThumbPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      engine.endThumbDrag();
      draggingRef.current = false;
      scheduleFade();
    },
    [engine, scheduleFade],
  );
  const handleThumbKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const scroller = scrollerRef.current;
      const readableViewportH = Math.max(
        0,
        (scroller?.clientHeight ?? 0) - viewportInsets.composerOverlayPx - viewportInsets.safeAreaBottomPx,
      );
      const scrollDeltaPx = keyboardScrollDelta(event.key, readableViewportH);
      if (scrollDeltaPx === undefined) return;

      event.preventDefault();
      revealScrollbar();
      synthesizeKeyboardDrag(scrollDeltaPx);
    },
    [revealScrollbar, scrollerRef, synthesizeKeyboardDrag, viewportInsets],
  );

  useEffect(() => {
    if (!isFinePointer) {
      clearFadeTimer();
      return undefined;
    }

    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const handleScrollActivity = () => revealScrollbar();
    const handleScrollKeyDown = (event: KeyboardEvent) => {
      if (isScrollActivityKey(event.key)) revealScrollbar();
    };

    scroller.addEventListener('wheel', handleScrollActivity, { passive: true });
    scroller.addEventListener('touchstart', handleScrollActivity, { passive: true });
    scroller.addEventListener('keydown', handleScrollKeyDown, { passive: true });

    return () => {
      scroller.removeEventListener('wheel', handleScrollActivity);
      scroller.removeEventListener('touchstart', handleScrollActivity);
      scroller.removeEventListener('keydown', handleScrollKeyDown);
      clearFadeTimer();
    };
  }, [clearFadeTimer, isFinePointer, revealScrollbar, scrollerRef]);

  if (!isFinePointer) return null;

  return (
    <div
      className="pointer-events-auto absolute top-2 right-1 bottom-2 z-(--stack-messages-sticky-header) w-2 rounded-full"
      data-feed-scrollbar-track
      onPointerDown={handleTrackPointerDown}
      onPointerEnter={handleTrackPointerEnter}
      onPointerLeave={handleTrackPointerLeave}
      ref={registerTrack}
    >
      <div
        aria-controls={scrollerId}
        aria-orientation="vertical"
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={scrollStatePercent(scrollState)}
        className={`feed-scrollbar-thumb w-full cursor-pointer rounded-full backdrop-blur transition-opacity motion-reduce:transition-none ${isVisible ? 'opacity-100 duration-0' : 'opacity-0 duration-300'}`}
        data-feed-scrollbar-thumb
        data-scrollbar-visible={isVisible}
        onBlur={handleThumbBlur}
        onFocus={handleThumbFocus}
        onKeyDown={handleThumbKeyDown}
        onPointerCancel={handleThumbPointerCancel}
        onPointerDown={handleThumbPointerDown}
        onPointerMove={handleThumbPointerMove}
        onPointerUp={handleThumbPointerUp}
        ref={registerThumb}
        role="scrollbar"
        tabIndex={0}
      />
    </div>
  );
}

function useFinePointer(): boolean {
  const [isFinePointer, setIsFinePointer] = useState(pointerFineMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(pointer: fine)');
    const handleChange = () => setIsFinePointer(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return isFinePointer;
}

function pointerFineMatches(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(pointer: fine)').matches;
}

function keyboardScrollDelta(key: string, readableViewportH: number): number | undefined {
  if (key === 'ArrowDown') return 40;
  if (key === 'ArrowUp') return -40;
  if (key === 'PageDown') return readableViewportH;
  if (key === 'PageUp') return -readableViewportH;
  return undefined;
}

function isScrollActivityKey(key: string): boolean {
  return (
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'PageUp' ||
    key === 'PageDown' ||
    key === 'Home' ||
    key === 'End' ||
    key === ' ' ||
    key === 'Space' ||
    key === 'Spacebar'
  );
}

function isScrollable(scroller: HTMLDivElement | null): boolean {
  return Boolean(scroller && scroller.scrollHeight - scroller.clientHeight > 0.5);
}

function scrollStatePercent(state: ScrollState): number {
  const scrollable = state.distanceFromTop + state.distanceFromBottom;
  if (scrollable <= 0) return state.atBottom ? 100 : 0;
  return Math.round(clampNumber(state.distanceFromTop / scrollable, 0, 1) * 100);
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
